<template>
  <main class="app-main">
    <div class="app-content-header">
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-6"><h3 class="mb-0">المشاريع العقارية</h3></div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-end">
              <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
              <li class="breadcrumb-item active" aria-current="page">المشاريع العقارية</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
    <div class="app-content">
      <div class="container-fluid">
        <div v-if="!checkAccess('projects_read')" class="alert alert-warning">ليس لديك صلاحية عرض المشاريع العقارية.</div>
        <template v-else>
          <div class="row mb-3">
            <div class="col text-end">
              <router-link v-if="checkAccess('projects_create')" to="/projects/create" class="btn btn-primary">
                <i class="bi bi-plus-circle me-1"></i> إضافة مشروع
              </router-link>
            </div>
          </div>
          <div class="row g-3 mb-3">
            <div v-for="card in statisticCards" :key="card.label" class="col-12 col-sm-6 col-xl">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
                    <span class="text-muted">{{ card.label }}</span>
                    <i :class="card.icon" class="text-primary fs-4" aria-hidden="true"></i>
                  </div>
                  <div class="fs-4 fw-bold text-primary">{{ card.value }}</div>
                  <div class="small text-muted mt-1">{{ card.hint }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="card shadow card-warning card-outline mt-3">
            <div class="card-header">
              <button class="filter-btn w-100 text-start collapse-toggle" type="button" data-bs-toggle="collapse" data-bs-target="#projectsFilterCard" aria-expanded="false" aria-controls="projectsFilterCard">
                <i class="fa-solid fa-magnifying-glass"></i> بحث وتصفية
              </button>
            </div>
            <div id="projectsFilterCard" class="collapse">
              <form @submit.prevent="filterSubmit">
                <div class="card-body">
                  <div class="row">
                    <div class="col-12 col-sm-6 col-lg-3 mb-3">
                      <label class="visually-hidden" for="projects-filter-title">اسم المشروع</label>
                      <input id="projects-filter-title" v-model.trim="filters.title" class="form-control" type="search" placeholder="اسم المشروع أو رقمه" />
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3 mb-3">
                      <label class="visually-hidden" for="projects-filter-mobile">جوال المالك</label>
                      <input id="projects-filter-mobile" v-model.trim="filters.mobile" class="form-control" type="tel" placeholder="جوال المالك" />
                    </div>
                    <div class="col-6 col-sm-4 col-lg-2 mb-3">
                      <label class="visually-hidden" for="projects-filter-status">الحالة</label>
                      <select id="projects-filter-status" v-model="filters.status" class="form-control">
                        <option value="">جميع الحالات</option>
                        <option v-for="(label, value) in statuses" :key="value" :value="value">{{ label }}</option>
                      </select>
                    </div>
                    <div class="col-6 col-sm-4 col-lg-2 mb-3">
                      <label class="visually-hidden" for="projects-filter-region">المنطقة</label>
                      <select id="projects-filter-region" v-model="filters.regionId" class="form-control" @change="getCitiesList()">
                        <option value="">جميع المناطق</option>
                        <option v-for="region in regionsList" :key="region.id" :value="region.id">{{ region.name }}</option>
                      </select>
                    </div>
                    <div class="col-6 col-sm-4 col-lg-2 mb-3">
                      <label class="visually-hidden" for="projects-filter-city">المدينة</label>
                      <select ref="citiesSelect" id="projects-filter-city" v-model="filters.cityId" class="form-control" :disabled="!filters.regionId || citiesLoading">
                        <option value="">جميع المدن</option>
                        <option v-for="city in citiesList" :key="city.id" :value="city.id">{{ city.name }}</option>
                      </select>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <label class="form-label" for="projects-filter-from">تاريخ الإنشاء من</label>
                      <input id="projects-filter-from" v-model="filters.dateFrom" type="date" class="form-control" :max="filters.dateTo || undefined" />
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <label class="form-label" for="projects-filter-to">تاريخ الإنشاء إلى</label>
                      <input id="projects-filter-to" v-model="filters.dateTo" type="date" class="form-control" :min="filters.dateFrom || undefined" />
                    </div>
                  </div>
                </div>
                <div class="card-footer">
                  <div class="row"><div class="col text-end">
                    <button type="submit" class="btn btn-warning btn-sm" :disabled="mainLoading"><i class="fa-solid fa-filter"></i> بحث وتصفية</button>
                    <button type="button" class="btn btn-light btn-sm ms-3" :disabled="mainLoading" @click="clearFilter"><i class="fa-solid fa-filter-circle-xmark"></i> إلغاء</button>
                  </div></div>
                </div>
              </form>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="card card-secondary card-outline mb-4 mt-5">
                <div class="card-header d-flex align-items-center gap-2">
                  <div class="card-title mb-0">سجل المشاريع العقارية</div>
                  <span class="badge rounded-pill bg-primary ms-auto">عدد النتائج ( {{ countRows }} )</span>
                </div>
                <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />
                <div class="card-body">
                  <mainLoader v-if="mainLoading" />
                  <template v-else>
                    <div v-if="!rows.length" class="text-center text-muted py-4">لا توجد مشاريع مطابقة للبحث.</div>
                    <div v-else class="table-responsive">
                      <table class="table table-striped align-middle">
                        <thead><tr><th>المشروع</th><th>المالك</th><th>المدينة</th><th>الحالة</th><th>العقارات / الوحدات</th><th>الإشغال</th><th>عمولة الشركة</th><th>نهاية العقد</th><th>العمليات</th></tr></thead>
                        <tbody>
                          <tr v-for="row in rows" :key="row.id">
                            <td><router-link :to="`/projects/update/${row.id}`" class="fw-bold">{{ row.title }}</router-link><div class="small text-muted">{{ row.code }}</div></td>
                            <td>{{ row.User?.name || '—' }}<div class="small text-muted" dir="ltr">{{ row.User?.mobile || '—' }}</div></td>
                            <td>{{ row.City?.name || '—' }}</td>
                            <td><span class="badge" :class="statusClass(row.effectiveStatus || row.status)">{{ statuses[row.effectiveStatus || row.status] || row.status }}</span></td>
                            <td>{{ number(row.stats?.containersCount) }} عقار متعدد الوحدات<div class="small text-muted">{{ number(row.stats?.unitsCount) }} وحدة / عقار</div></td>
                            <td>{{ number(row.stats?.occupancy) }}%</td>
                            <td>{{ number(row.commissionValue) }}{{ row.commissionType === 'percent' ? '%' : ' ر.س' }}</td>
                            <td class="text-nowrap">{{ row.endDate ? $shortDate(row.endDate) : '—' }}</td>
                            <td class="text-nowrap">
                              <router-link :to="`/projects/update/${row.id}`" class="btn btn-light btn-sm me-2" :aria-label="`عرض مشروع ${row.title}`"><i class="fa-regular fa-eye"></i></router-link>
                              <button v-if="checkAccess('projects_delete')" type="button" class="btn btn-danger btn-sm" :disabled="deletingId !== null" :aria-label="`حذف مشروع ${row.title}`" @click="deleteRow(row)"><i class="bi bi-trash"></i></button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </template>
                </div>
                <div v-if="pages.length > 1" class="card-footer">
                  <ul class="pagination mb-0 flex-wrap">
                    <li v-if="currentPage > 1" class="page-item"><button type="button" class="page-link" :disabled="mainLoading" @click="goToPage(currentPage - 1)">السابق</button></li>
                    <li v-for="page in pages" :key="page" class="page-item"><button type="button" class="page-link" :class="{ active: page == currentPage }" :aria-current="page == currentPage ? 'page' : undefined" :disabled="mainLoading" @click="goToPage(page)">{{ page }}</button></li>
                    <li v-if="currentPage < pages[pages.length - 1]" class="page-item"><button type="button" class="page-link" :disabled="mainLoading" @click="goToPage(currentPage + 1)">التالي</button></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import { toast, checkAccess } from '@/helpers/tools';
import $ from 'jquery';
window.$ = $;
window.jQuery = $;
import 'select2';
import 'select2/dist/css/select2.min.css';

export default {
  name: 'ProjectsList',
  data() {
    const query = this.$route.query;
    const filters = { title: query.title || '', mobile: query.mobile || '', status: query.status || '', regionId: query.regionId || '', cityId: query.cityId || '', dateFrom: query.dateFrom || '', dateTo: query.dateTo || '' };
    return {
      mainLoading: true, hasError: false, message: '', errorNumber: 0,
      rows: [], stats: {}, countRows: 0, pages: [], currentPage: Math.max(1, Number(query.page) || 1),
      filters, appliedFilters: { ...filters }, statuses: {}, regionsList: [], citiesList: [],
      citiesLoading: false, deletingId: null, requestSerial: 0, citySerial: 0, disposed: false,
    };
  },
  computed: {
    statisticCards() {
      return [
        { label: 'إجمالي المشاريع', value: this.number(this.stats.projectsCount), icon: 'bi bi-buildings', hint: `${this.number(this.stats.unitsCount)} وحدة / عقار` },
        { label: 'معدل الإشغال الإجمالي', value: `${this.number(this.stats.occupancy)}%`, icon: 'bi bi-pie-chart', hint: `${this.number(this.stats.rented)} وحدة مؤجرة` },
        { label: 'الإيراد السنوي المتوقع', value: this.money(this.stats.annualExpected), icon: 'bi bi-graph-up-arrow', hint: 'من عقود الإيجار السارية' },
        { label: 'المحصل الفعلي', value: this.money(this.stats.collectedAmount), icon: 'bi bi-cash-stack', hint: `متأخرات: ${this.money(this.stats.lateAmount)}` },
        { label: 'عمولات الإدارة المستحقة', value: this.money(this.stats.companyCommission), icon: 'bi bi-percent', hint: 'حسب اتفاق كل مشروع' },
      ];
    },
  },
  watch: {
    citiesList() { this.$nextTick(this.applySelect2Cities); },
    'filters.cityId'() { this.$nextTick(this.applySelect2Cities); },
    citiesLoading() { this.$nextTick(this.applySelect2Cities); },
    mainLoading(value) { if (!value) this.$nextTick(this.applySelect2Cities); },
  },
  methods: {
    applySelect2Cities() {
      if (this.disposed || !this.$refs.citiesSelect) return;
      const vm = this;
      const $element = $(this.$refs.citiesSelect);
      if (!$element.hasClass('select2-hidden-accessible')) {
        $element.select2({width: '100%', dir: 'rtl', placeholder: 'جميع المدن', allowClear: true});
        $element.on('change.projectFilterCitySelect2', function() {
          const value = $(this).val() ? Number($(this).val()) : '';
          if (String(vm.filters.cityId) !== String(value)) vm.filters.cityId = value;
        });
      }
      $element.val(this.filters.cityId || '').trigger('change.select2');
    },
    destroySelect2Cities() {
      const $element = $(this.$refs.citiesSelect);
      $element.off('.projectFilterCitySelect2');
      if ($element.hasClass('select2-hidden-accessible')) $element.select2('destroy');
    },
    checkAccess,
    number(value) { return Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 2 }); },
    money(value) { return `${this.number(value)} ر.س`; },
    statusClass(status) { return status === 'active' ? 'bg-success' : status === 'expired' ? 'bg-danger' : 'bg-warning text-dark'; },
    filterSubmit() {
      if (this.filters.dateFrom && this.filters.dateTo && this.filters.dateTo < this.filters.dateFrom) {
        this.message = 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية.'; this.errorNumber = 0; this.hasError = true; return;
      }
      this.appliedFilters = { ...this.filters };
      this.currentPage = 1;
      this.getList();
    },
    clearFilter() {
      this.filters = { title: '', mobile: '', status: '', regionId: '', cityId: '', dateFrom: '', dateTo: '' };
      this.citiesList = [];
      this.citySerial += 1;
      this.citiesLoading = false;
      this.filterSubmit();
    },
    getList() {
      if (!checkAccess('projects_read')) { this.mainLoading = false; return; }
      const serial = ++this.requestSerial;
      this.mainLoading = true;
      this.hasError = false;
      const params = { page: this.currentPage };
      Object.entries(this.appliedFilters).forEach(([key, value]) => { if (value) params[key] = value; });
      this.$router.replace({ query: params }).catch(() => {});
      this.$http.get('/api/admin/projects/list', { params }).then(({ data }) => {
        if (this.disposed || serial !== this.requestSerial) return;
        this.rows = data.rows || [];
        this.countRows = data.countRows || 0;
        this.pages = data.pages || [];
        this.stats = data.stats || {};
        this.statuses = data.statuses || {};
        this.regionsList = data.regionsList || [];
      }).catch(err => {
        if (this.disposed || serial !== this.requestSerial) return;
        [this.message, this.errorNumber] = this.$globalError(err, true);
        this.hasError = true;
      }).finally(() => { if (!this.disposed && serial === this.requestSerial) this.mainLoading = false; });
    },
    getCitiesList(change = true) {
      const serial = ++this.citySerial;
      this.citiesList = [];
      if (change) this.filters.cityId = '';
      if (!this.filters.regionId) { this.citiesLoading = false; return; }
      this.citiesLoading = true;
      this.$http.get('/api/admin/cities/list', { params: { regionId: this.filters.regionId, all: 1 } }).then(({ data }) => {
        if (!this.disposed && serial === this.citySerial) this.citiesList = data.cities || [];
      }).catch(err => {
        if (this.disposed || serial !== this.citySerial) return;
        [this.message, this.errorNumber] = this.$globalError(err, true); this.hasError = true;
      }).finally(() => { if (!this.disposed && serial === this.citySerial) this.citiesLoading = false; });
    },
    async deleteRow(row) {
      if (this.deletingId !== null) return;
      const result = await this.$swal.fire({ title: `هل تريد تأكيد حذف مشروع «${row.title}»؟`, text: 'لا يمكن استعادته بعد الحذف.', showCancelButton: true, confirmButtonText: 'حذف', cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3741' });
      if (!result.isConfirmed || this.disposed) return;
      this.deletingId = row.id;
      try {
        await this.$http.delete(`/api/admin/projects/delete/${row.id}`);
        if (this.disposed) return;
        toast('تم حذف المشروع بنجاح', 'success');
        if (this.rows.length === 1 && this.currentPage > 1) this.currentPage -= 1;
        this.getList();
      } catch (err) {
        if (!this.disposed) { [this.message, this.errorNumber] = this.$globalError(err, true); this.hasError = true; }
      } finally { if (!this.disposed) this.deletingId = null; }
    },
    goToPage(page) { if (this.mainLoading) return; this.currentPage = Number(page); this.getList(); },
  },
  created() { this.getList(); if (this.filters.regionId) this.getCitiesList(false); },
  mounted() { this.$nextTick(this.applySelect2Cities); },
  beforeUnmount() { this.destroySelect2Cities(); this.disposed = true; this.requestSerial += 1; this.citySerial += 1; },
};
</script>
